import { ROLE_LABELS, type WorkspaceRole } from "@onadssuper/validators";
import { Check, ChevronDown } from "lucide-react";
import { Select as RadixSelect } from "radix-ui";
import { cn } from "../../lib/utils";

interface SelectProps<T extends string> {
  value: T | undefined;
  onValueChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  className?: string;
}

export const Select = <T extends string>({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  className,
  ...aria
}: SelectProps<T>) => (
  <RadixSelect.Root
    // "" (not undefined) shows the placeholder and keeps the select controlled.
    value={value ?? ""}
    onValueChange={(v) => {
      const option = options.find((o) => o.value === v);
      if (option) onValueChange(option.value);
    }}
    disabled={disabled}
  >
    <RadixSelect.Trigger
      {...aria}
      className={cn(
        "inline-flex h-10 w-full items-center justify-between gap-2 rounded-md border border-border-strong bg-surface px-3 text-sm",
        "disabled:cursor-not-allowed disabled:opacity-60 data-[placeholder]:text-subtle",
        className,
      )}
    >
      <RadixSelect.Value placeholder={placeholder} />
      <RadixSelect.Icon>
        <ChevronDown className="size-4 text-subtle" />
      </RadixSelect.Icon>
    </RadixSelect.Trigger>
    <RadixSelect.Portal>
      <RadixSelect.Content
        position="popper"
        sideOffset={4}
        className="z-50 min-w-[var(--radix-select-trigger-width)] rounded-md border border-border bg-surface p-1 shadow-lg"
      >
        <RadixSelect.Viewport>
          {options.map((o) => (
            <RadixSelect.Item
              key={o.value}
              value={o.value}
              className="relative flex cursor-pointer items-center rounded-sm py-1.5 pr-2 pl-7 text-sm outline-none select-none data-[highlighted]:bg-surface-muted"
            >
              <RadixSelect.ItemIndicator className="absolute left-2">
                <Check className="size-3.5" />
              </RadixSelect.ItemIndicator>
              <RadixSelect.ItemText>{o.label}</RadixSelect.ItemText>
            </RadixSelect.Item>
          ))}
        </RadixSelect.Viewport>
      </RadixSelect.Content>
    </RadixSelect.Portal>
  </RadixSelect.Root>
);

export const roleOptions = (roles: readonly WorkspaceRole[]) =>
  roles.map((r) => ({ value: r, label: ROLE_LABELS[r] }));

export const RoleSelect = (
  props: Omit<SelectProps<WorkspaceRole>, "options"> & { roles: readonly WorkspaceRole[] },
) => {
  const { roles, ...rest } = props;
  return (
    <Select
      {...rest}
      options={roleOptions(roles)}
      placeholder={rest.placeholder ?? "Choose a role"}
    />
  );
};
